import { t } from '@hefe/i18n';
import { ModalsSlice } from '@hefe/store';
import { colors } from '@hefe/ui';
import { Modal, Typography } from 'antd';
import { PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import EventDetailsComponent from '../../containers/events/components/event-details/event-details/event-details.component';
import GameDetailsComponent from '../../containers/events/components/game-details/game-details.component';
import PracticeDetailsComponent from '../../containers/events/components/practice-details/practice-details.component';
import TutorialComponent from '../../containers/tutorials/components/tutorial/tutorial.component';
import { useAppDispatch } from '../../store/store';

/* eslint-disable-next-line */
export interface ModalProviderProps {}

export default function ModalProvider(
  props: PropsWithChildren<ModalProviderProps>
) {
  const dispatch = useAppDispatch();
  const modalsState = useSelector(ModalsSlice.modalsState);

  const renderDialog = () => {
    const { dialog } = modalsState;

    return (
      <Modal
        confirmLoading={dialog.loading}
        open={dialog.open}
        title={dialog.title}
        onCancel={() => dispatch(ModalsSlice.modalsActions.closeDialog())}
        onOk={() => {
          dispatch(ModalsSlice.modalsActions.setDialogLoading(true));
          dialog.onOk(() => {
            dispatch(ModalsSlice.modalsActions.closeDialog());
          });
        }}
        okText={t('yes')}
        cancelText={t('cancel')}
      >
        <Typography.Text>{dialog.text}</Typography.Text>
      </Modal>
    );
  };

  const renderModal = () => {
    const { modal } = modalsState;

    const bodyStyle = modal.lightBackground
      ? { backgroundColor: colors.lightgrey }
      : {};

    const okText = modal.okText || modal.formId ? t('save') : t('ok');

    return (
      <Modal
        styles={{
          body: bodyStyle,
        }}
        confirmLoading={modal.loading}
        open={modal.open}
        title={modal.title}
        width={modal.width || 520}
        zIndex={modal.zIndex ?? 1000}
        closable={modal.closable}
        onCancel={() => dispatch(ModalsSlice.modalsActions.closeModal())}
        onOk={() => {
          if (!modal.formId) {
            dispatch(ModalsSlice.modalsActions.setModalLoading(true));
          }
          modal.onOk(() => {
            dispatch(ModalsSlice.modalsActions.closeModal());
          });
        }}
        okButtonProps={{
          form: modal.formId || undefined,
          htmlType: modal.formId ? 'submit' : 'button',
          hidden: modal.hideOkButton,
        }}
        okText={okText}
        cancelButtonProps={{ hidden: modal.hideCancelButton }}
        cancelText={t('cancel')}
        {...(modal.hideOkButton && modal.hideCancelButton
          ? { footer: null }
          : {})}
      >
        {modal.component}
      </Modal>
    );
  };

  const renderGameModal = () => {
    const { gameModal } = modalsState;

    return (
      <Modal
        confirmLoading={gameModal.loading}
        open={gameModal.open}
        title={t('games.details')}
        width="100vw"
        footer={null}
        onCancel={() => dispatch(ModalsSlice.modalsActions.closeGameModal())}
      >
        <GameDetailsComponent />
      </Modal>
    );
  };

  const renderPracticeModal = () => {
    const { practiceModal } = modalsState;

    return (
      <Modal
        styles={{
          body: { backgroundColor: colors.lightgrey },
        }}
        confirmLoading={practiceModal.loading}
        open={practiceModal.open}
        title={t('practices.details')}
        width="100vw"
        footer={null}
        onCancel={() =>
          dispatch(ModalsSlice.modalsActions.closePracticeModal())
        }
      >
        <PracticeDetailsComponent />
      </Modal>
    );
  };

  const renderEventModal = () => {
    const { eventModal } = modalsState;

    return (
      <Modal
        confirmLoading={eventModal.loading}
        open={eventModal.open}
        title={t('events.details')}
        width="100vw"
        footer={null}
        onCancel={() => dispatch(ModalsSlice.modalsActions.closeEventModal())}
      >
        <EventDetailsComponent />
      </Modal>
    );
  };

  const renderTutorialModal = () => {
    const { tutorialModal } = modalsState;

    return (
      <Modal
        confirmLoading={tutorialModal.loading}
        open={tutorialModal.open}
        title={t('tutorials.singular')}
        width="70vw"
        footer={null}
        closable={false}
      >
        <TutorialComponent id={tutorialModal.id} />
      </Modal>
    );
  };

  return (
    <div>
      {props.children}
      {renderDialog()}
      {renderModal()}
      {renderGameModal()}
      {renderPracticeModal()}
      {renderEventModal()}
      {renderTutorialModal()}
    </div>
  );
}
